import { Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import {
  EmpowerScheduledTask,
  EmpowerTask,
} from "../../interfaces/models/EmpowerTask";
import EmpowerTaskSelection from "./EmpowerTaskSelection";
import {
  RRuleEndAfterCountFieldName,
  RRuleEndNever,
  RRuleEndOnDateFieldName,
} from "./Scheduling/EmpowerMeFormFieldConstants";
import EmpowerTaskCompletion from "./EmpowerTaskCompletion";
import { useRef, useState } from "react";
import { Formik, FormikProps } from "formik";
import { Frequency, RRule } from "rrule";
import { useMutation } from "@apollo/client";
import { CreateEmpowerMeTask, CreateEmpowerMeTaskBundle } from "../../graphql/empower-me/mutations/CreateEmpowerMeTask";
import EmpowerScheduleSelection from "./Scheduling/EmpowerScheduleSelection";
import "./EmpowerMePage.scss";
import AddEmpowerMeTaskValidator from "../../validators/AddEmpowerMeTaskValidator"
import { UserActivityTypeWithGroup } from "../../helper/constants"
import { AddActivityHookExecute, ModelEnums } from "../../helper/addUserActivity"
import moment from "moment";
import { EmpowerMeBundle } from "../../interfaces/models/EmpowerMeBundle";
import toast from "react-hot-toast";
import { parseRRuleToObject } from "../../helper/rruleUtilities";

interface ModalProps {
  isOpen: boolean;
  toggle: () => void;
  editTask?: EmpowerScheduledTask;
  refetchTasks: () => void;
  empowerTasks: EmpowerScheduledTask[];
}

export interface EmpowerScheduleForm {
  taskId?: string;
  date?: string;
  time?: string;
  repeatInterval?: number;
  repeatFrequency?: Frequency; // RRule.Frequency: DAILY, WEEKLY, MONTHLY
  rRuleEndRadio?: string;
  endRRuleOnDate?: string;
  endRRuleAfterCount?: number;
}

const modalHeaderTexts = [
  "Please select an empower me task",
  "Please schedule a time for your empower me task",
  "",
];

const SelectionTypes = {
  NONE: 0,
  SINGLE: 1,
  BUNDLE: 2,
}

export default function EmpowerMeTaskModal({
  isOpen,
  toggle,
  refetchTasks,
  empowerTasks
}: ModalProps) {
  const formRef = useRef<FormikProps<EmpowerScheduleForm>>(null);
  const [modalStep, setModalStep] = useState<number>(0);
  const [selectedTask, setSelectedTask] = useState<EmpowerTask | null>(null);
  const [selectedBundle, setSelectedBundle] = useState<EmpowerMeBundle | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>(null);
  const [selectionType, setSelectionType] = useState<number>(SelectionTypes.NONE);

  // Use this state to determine which fields should be validated on submit and page changes
  const dateNow = useRef<Date>(new Date());
  const [recurrenceEnabled, setRecurrenceEnabled] = useState<boolean>(false);
  const [refetchUsersEmpowerTasks, setRefetchUsersEmpowerTasks] =
    useState<boolean>(false);
  const [bundleTimes, setBundleTimes] = useState<string[]>([
    "9:00",
    "10:00",
    "11:00"
  ]);
  const [bundleDate, setBundleDate] = useState<string>(moment().format("yyyy-MM-DD"));
  // Mutations
  const [
    createEmpowerMeTask,
    { loading: creatingTask, error: createTaskError },
  ] = useMutation(CreateEmpowerMeTask);
  const [
    createEmpowerMeTaskBundle,
    { loading: creatingTaskBundle, error: createTaskBundleError },
  ] = useMutation(CreateEmpowerMeTaskBundle);
  

  const { executeAddActivity } = AddActivityHookExecute();

  function enableRecurrence(enable: boolean) {
    setRecurrenceEnabled(enable);
  }

  const validateDate = (date: string, time : string, index : number | null) => {

    if(!date && !time){
      return "Please select a date and time";
    }

    if(index !== null) {
      const getCurrent = moment(bundleDate + " " + bundleTimes[index], "YYYY-MM-DD HH:mm").toDate();
      let duplicateCount = 0;
      for(let x = 0; x < bundleTimes.length; x++) {
        const bundleDateItem = moment(bundleDate + " " + bundleTimes[x], "YYYY-MM-DD HH:mm").toDate();
        if(bundleDateItem.getTime() <= dateNow.current.getTime() ) {
          return "Please choose a time in the future"
        }
        if(getCurrent.getTime() === bundleDateItem.getTime()) {
          duplicateCount++;
        }
      }
      if(duplicateCount > 1) {
          return "Time overlapped with other bundle tasks"
      }
    }

    const getDate = moment(date + " " + time, "YYYY-MM-DD HH:mm");
    const haveOverlappedMessage = "A task is already scheduled for this time";
    for (let x = 0; x < empowerTasks.length; x++) {
      const task = empowerTasks[x];

      if(task.deleted_at) continue;

      const targetDate = getDate.toDate();

      // Check non-repeating tasks
      const taskDate = moment(new Date(task.start_date)).toDate();

      if (targetDate.getTime() === taskDate.getTime()) {
        return haveOverlappedMessage;
      }
      if (task.recurrence_rule !== null) {
        const optionRules = parseRRuleToObject(task.recurrence_rule);
        const startDate = new Date(task.start_date);
        if(optionRules.origOptions.freq === RRule.DAILY && optionRules.options.interval === 1 && optionRules.origOptions.until) {
          const until = optionRules.origOptions.until;

          if(targetDate >= startDate && targetDate <= until && moment(startDate).format("HH:mm") === moment(targetDate).format("HH:mm")) {
            return haveOverlappedMessage;
          }
        }
        else if(optionRules.origOptions.freq === RRule.DAILY && optionRules.options.interval === 1 && optionRules.origOptions.until === undefined) {
          //check for interval based on the frequency
          if(moment(startDate).format("HH:mm") === moment(targetDate).format("HH:mm")) {
            return haveOverlappedMessage;
          }
        }
        else {
          const rule = new RRule({
            freq: optionRules.options.freq,
            interval: optionRules.options.interval,
            dtstart: startDate,
            until : optionRules.options.until
          });
          let upcomming = null;
  
          if(!optionRules.options.until) {
            const now = new Date();
            const oneMonthFromNow = new Date();
            oneMonthFromNow.setFullYear(now.getMonth() + 1);
            
            upcomming = rule.between(now, oneMonthFromNow, true);
          }
          else {
            upcomming = rule.between(startDate, optionRules.options.until, true);
          }
  
          if(upcomming.some(
            (occurrence : Date) => occurrence.getTime() === targetDate.getTime()
          )) {
            return haveOverlappedMessage;
          }
        }
      }
    }

    return null;
  }

  function resetModal() {
    // Clear states on success
    setSelectedTask(null);
    setSelectedBundle(null);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectionType(SelectionTypes.NONE);
    formRef?.current?.resetForm();
    setModalStep(0);
    // Update the user's empower me tasks in the table if they've created a task
    if (refetchUsersEmpowerTasks) {
      refetchTasks();
      setRefetchUsersEmpowerTasks(false);
    }
  }

  async function handleNextStep() {
    if (modalStep === 0) {
      if (selectedTask !== null || selectedBundle !== null) {
        setModalStep((prevStep) => (prevStep += 1));
      }
    }
    if (modalStep === 1) {
      formRef?.current.handleSubmit();
    }
  }

  async function submitEmpowerMeForm(values: EmpowerScheduleForm) {
    // Construct start_date in correct format for mutation
    const start_date_iso = new Date(
      values.date + " " + values.time
    ).toISOString();

    let rrule = null;
    if (recurrenceEnabled) {
      // Construct RRULE to be passed in mutation
      try {
        const rRuleEndCondition = values.rRuleEndRadio;
        rrule = new RRule({
          freq: values.repeatFrequency,
          interval: values.repeatInterval,
          dtstart: undefined,
          until:
            rRuleEndCondition === RRuleEndOnDateFieldName
              ? new Date(Date.parse(values.endRRuleOnDate))
              : undefined,
          count:
            rRuleEndCondition === RRuleEndAfterCountFieldName
              ? values.endRRuleAfterCount
              : undefined,
        }).toString();
      } catch (error) {
        console.log(
          "Error ocurred whilst constructing recurrence rule string: ",
          error
        );
      }
    }
    try {
      if(selectedBundle !== null) {
        let tasks_reformated = [];
        for(let x = 0; x < selectedBundle.tasks.length; x++) {
          const em_task_id = selectedBundle.tasks[x].empower_me_task_details._id;
          const get_start_date = new Date(
            bundleDate + " " + bundleTimes[x]
          ).toISOString();

          if(validateDate(bundleDate, bundleTimes[x], x)) {
            return;
          }
          
          tasks_reformated.push({
            task_id: em_task_id,
            start_date: get_start_date,
            bundle_id : selectedBundle._id,
            recurrence_rule: rrule,
          });
        }


        const result =  await createEmpowerMeTaskBundle({
          variables: {
            tasks: tasks_reformated,
            bundle_id: selectedBundle._id,
          },
        })
        .then((res) => res)
        .catch( (error) => {
          return {
            errors : error.graphQLErrors
          };
        });
        if(result.errors) {
          toast.error(result.errors[0].message);
          return;
        }

        executeAddActivity({
          type : UserActivityTypeWithGroup.EM_CREATE.id, 
          type_group : UserActivityTypeWithGroup.EM_CREATE.group,
          on_model : ModelEnums.EM_BUNDLE,
          related_id : selectedBundle._id,
        });
      }
      else {
        if(validateDate(values.date, values.time, null)) {
          return;
        }

        await createEmpowerMeTask({
          variables: {
            task_id: selectedTask._id,
            start_date: start_date_iso,
            recurrence_rule: rrule,
          },
        });

        executeAddActivity({
          type : UserActivityTypeWithGroup.EM_CREATE.id, 
          type_group : UserActivityTypeWithGroup.EM_CREATE.group,
          on_model : ModelEnums.EM,
          related_id : selectedTask._id,
        });
      }
      setModalStep((prevStep) => (prevStep += 1));
      // Don't unnecessarily refresh if there was an error
      setRefetchUsersEmpowerTasks(true);
    } catch (error) {
      console.log(
        "Error occurred whilst creating/updating user's scheduled empower me task:",
        error
      );
    }
  }

  const NextStepButton = () => {
    let label = "Next";
    let shouldDisable = selectedTask === null && selectedBundle === null; // Disable Next button unless a task has been selected
    let onClicked = () => {
      handleNextStep();
    };
    // Final step
    if (modalStep === 2) {
      return (
        <Button
          color="primary"
          onClick={(e) => {
            resetModal();
            toggle();
          }}
          disabled={creatingTask || creatingTaskBundle}
        >
          Done
        </Button>
      );
    } else if (modalStep === 1) {
      label = "Schedule";
      shouldDisable = false; // TODO: this should disable until the user specifies a scheduled time
      shouldDisable = creatingTask || creatingTaskBundle;
    }
    return (
      <Button color="primary" onClick={onClicked} disabled={shouldDisable}>
        {label}
      </Button>
    );
  };

  const PrevStepButton = () => {
    const hideButton = modalStep === 0 || modalStep === 2;
    return (
      <Button
        className={`${hideButton && "invisible"}`}
        disabled={hideButton}
        color="secondary"
        onClick={(e) => {
          setModalStep((prevStep) => (prevStep -= 1));
        }}
      >
        Go Back
      </Button>
    );
  };

  // Construct initial values to be passed to form
  const initialValues: EmpowerScheduleForm = {
    taskId: null,
    date: moment().format("yyyy-MM-DD"),
    time: moment().format("HH:mm"),
    repeatInterval: null,
    repeatFrequency: Frequency.DAILY,
    rRuleEndRadio: RRuleEndNever,
    endRRuleOnDate: undefined,
    endRRuleAfterCount: undefined,
  };

  return (
    <>
      <Modal modalClassName="custom-modal" isOpen={isOpen}>
        <ModalHeader
          className="pt-3 pb-0 px-4"
          toggle={() => {
            if (!creatingTask) {
              toggle();
              if (modalStep === 2) {
                resetModal();
              }
            }
          }}
        >
          <p className="text-lg">{modalHeaderTexts[modalStep]}</p>
        </ModalHeader>
        <ModalBody className="d-flex flex-column justify-content-between overflow-auto pt-2">
          <div className="custom-modal-body">
            <Formik
              innerRef={formRef}
              initialValues={initialValues}
              validateOnBlur={false}
              validationSchema={AddEmpowerMeTaskValidator}
              onSubmit={async (values) => {
                await submitEmpowerMeForm(values);
              }}
            >
              <>
                {modalStep === 0 && (
                  <EmpowerTaskSelection
                    select={setSelectedTask}
                    currentSelection={selectedTask}
                    selectBundle={setSelectedBundle}
                    currentSelectionBundle={selectedBundle}
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                    selectedSubCategory={selectedSubCategory}
                    setSelectedSubCategory={setSelectedSubCategory}
                    selectionType={selectionType}
                    setSelectionType={setSelectionType}
                  />
                )}
                {modalStep === 1 && (
                  <EmpowerScheduleSelection
                    recurrenceEnabled={recurrenceEnabled}
                    enableRecurrence={enableRecurrence}
                    currentSelection={selectedTask}
                    currentSelectionBundle={selectedBundle}
                    setBundleTimes={setBundleTimes}
                    bundleTimes={bundleTimes}
                    bundleDate={bundleDate} 
                    setBundleDate={setBundleDate}
                    validateDate={validateDate}
                  />
                )}
                {modalStep === 2 && (
                  <EmpowerTaskCompletion
                    loading={creatingTask}
                    error={createTaskError || createTaskBundleError}
                  />
                )}
              </>
            </Formik>
          </div>
          <div className="modal-step-buttons">
            <PrevStepButton />
            <NextStepButton />
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}
